import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Zap, Target, Gem } from 'lucide-react';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { useSubscriptionStatus } from '../../../hooks/useSubscriptionStatus';
import { useSubscriptionStatusContext } from '../../../hooks/SubscriptionStatusContext';
import { useFadeInOnScroll } from '../../../hooks/useFadeInOnScroll';
import { PLAN_PRICING, CURRENCY_SYMBOLS } from '../../utils/constants';
import type { PlanType } from '../../../components/SubscriptionModal';
import { Container, AnimatedContainer, SectionHeader, EnhancedSectionTitle, SectionSubtitle } from '../../styles/sharedComponents';
import PricingCard from './PricingCard';
import { PricingSection, PricingGrid } from './styles';

const SubscriptionModal = lazy(() => import('../../../components/SubscriptionModal'));

const Pricing: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const [msg, setMsg] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('mensal');
  const status = useSubscriptionStatus(user);
  const { refresh: refreshGlobal } = useSubscriptionStatusContext();
  const refreshGlobalRef = useRef(refreshGlobal);
  const [usdRate, setUsdRate] = useState<number | null>(null);
  const currencyCode = t('pricing.currencyCode');
  const activeCurrencyCode = currencyCode === 'USD' ? 'USD' : 'BRL';
  const isUsdPricing = activeCurrencyCode === 'USD';
  const currencySymbol = CURRENCY_SYMBOLS[activeCurrencyCode] ?? activeCurrencyCode;

  const [pricingRef, pricingVisible] = useFadeInOnScroll<HTMLElement>({
    threshold: 0.2,
    rootMargin: '0px 0px -10% 0px',
    triggerOnce: true,
    reverseOnExit: false,
    delay: 0,
    direction: 'up',
    distance: 30,
  });

  useEffect(() => {
    refreshGlobalRef.current = refreshGlobal;
  }, [refreshGlobal]);

  useEffect(() => {
    if (activeCurrencyCode !== 'USD') {
      setUsdRate(null);
      return;
    }

    let isMounted = true;

    const fetchRate = async () => {
      if (typeof window === 'undefined') {
        return;
      }
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/BRL');
        if (!response.ok) {
          throw new Error('Failed to fetch USD exchange rate');
        }
        const data = await response.json();
        const rate = data?.rates?.USD;
        if (typeof rate === 'number' && isFinite(rate) && isMounted) {
          setUsdRate(rate);
        }
      } catch (error) {
        console.error('[Pricing] Failed to fetch USD exchange rate', error);
      }
    };

    fetchRate();
    const intervalId = window.setInterval(fetchRate, 60 * 60 * 1000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [activeCurrencyCode]);

  const convertAmount = (amount: number): number | null => {
    if (isUsdPricing) {
      if (usdRate === null) {
        return null;
      }
      return amount * usdRate;
    }
    return amount;
  };

  const formatPrice = (value: number | null): string => {
    if (value === null) return '...';
    return Math.round(value).toString();
  };

  const handleOpenModal = (planType: PlanType) => {
    if (!user) {
      setMsg(t('pricing.loginRequired'));
      return;
    }
    setSelectedPlan(planType);
    setShowModal(true);
  };

  // Preços convertidos
  const monthlyPrice = formatPrice(convertAmount(PLAN_PRICING.monthly.price));

  const quarterlyPrice = formatPrice(convertAmount(PLAN_PRICING.quarterly.price));
  const quarterlyInstallment = formatPrice(convertAmount(PLAN_PRICING.quarterly.installment));

  const yearlyPrice = formatPrice(convertAmount(PLAN_PRICING.yearly.price));
  const yearlyInstallment = formatPrice(convertAmount(PLAN_PRICING.yearly.installment));

  return (
    <PricingSection id="pricing" ref={pricingRef} $isVisible={pricingVisible}>
      <AnimatedContainer $isVisible={pricingVisible}>
        <SectionHeader>
          <EnhancedSectionTitle>{t('pricing.title')}</EnhancedSectionTitle>
          <SectionSubtitle>
            {t('pricing.subtitle')}
          </SectionSubtitle>
        </SectionHeader>

        <PricingGrid>
          {/* Mensal - Preço simples */}
          <PricingCard
            name={t('pricing.monthly.name')}
            isMonthly={true}
            currencySymbol={currencySymbol}
            price={monthlyPrice}
            billing={t('pricing.monthly.billing')}
            paymentInfo={t('pricing.monthly.payment')}
            features={t('pricing.monthly.features', { returnObjects: true }) as string[]}
            onSubscribe={() => handleOpenModal('mensal')}
            ctaText={status.plano ? t('pricing.monthly.ctaActive') : t('pricing.monthly.cta')}
            urgencyIcon={<Zap />}
            urgencyText={t('pricing.monthly.urgency')}
            isDisabled={!!status.plano}
          />

          {/* Trimestral - Parcelado primeiro */}
          <PricingCard
            featured
            badge={t('pricing.quarterly.badge')}
            name={t('pricing.quarterly.name')}
            currencySymbol={currencySymbol}
            billing={t('pricing.quarterly.billing')}
            installmentPrefix={t('pricing.quarterly.installmentPrefix')}
            installmentValue={quarterlyInstallment}
            installmentSuffix={t('pricing.quarterly.installmentSuffix')}
            cashLabel={t('pricing.quarterly.cashLabel')}
            cashPrice={quarterlyPrice}
            cashSuffix={t('pricing.quarterly.cashSuffix')}
            features={t('pricing.quarterly.features', { returnObjects: true }) as string[]}
            onSubscribe={() => handleOpenModal('trimestral')}
            ctaText={status.plano ? t('pricing.quarterly.ctaActive') : t('pricing.quarterly.cta')}
            urgencyIcon={<Target />}
            urgencyText={t('pricing.quarterly.urgency')}
            isDisabled={!!status.plano}
            sparklesFeatureIndex={0}
          />

          {/* Anual - Parcelado primeiro */}
          <PricingCard
            name={t('pricing.yearly.name')}
            currencySymbol={currencySymbol}
            billing={t('pricing.yearly.billing')}
            installmentPrefix={t('pricing.yearly.installmentPrefix')}
            installmentValue={yearlyInstallment}
            installmentSuffix={t('pricing.yearly.installmentSuffix')}
            cashLabel={t('pricing.yearly.cashLabel')}
            cashPrice={yearlyPrice}
            cashSuffix={t('pricing.yearly.cashSuffix')}
            features={t('pricing.yearly.features', { returnObjects: true }) as string[]}
            onSubscribe={() => handleOpenModal('anual')}
            ctaText={status.plano ? t('pricing.yearly.ctaActive') : t('pricing.yearly.cta')}
            urgencyIcon={<Gem />}
            urgencyText={t('pricing.yearly.urgency')}
            isDisabled={!!status.plano}
            sparklesFeatureIndex={0}
          />
        </PricingGrid>

        {msg && (
          <div style={{
            marginTop: '24px',
            padding: '12px 24px',
            background: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid rgba(255, 68, 68, 0.2)',
            borderRadius: '8px',
            color: '#ff4444',
            fontSize: '14px',
            maxWidth: '400px',
            margin: '24px auto 0',
            textAlign: 'center'
          }}>
            {msg}
          </div>
        )}

        <Suspense fallback={<div style={{ display: 'none' }} />}>
          <SubscriptionModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            userEmail={user?.email || undefined}
            planType={selectedPlan}
          />
        </Suspense>
      </AnimatedContainer>
    </PricingSection>
  );
};

export default Pricing;
